import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueries } from '@tanstack/react-query';
import type { MetricsSummary, MonthlyTrendPoint } from '@/application/metrics';
import { buildCajaMovimientosHref } from '@/application/metrics';
import { getMetricsSummary, getMonthlyTrend } from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { useAuth } from '@/presentation/contexts/useAuth';
import { previousMonthOf } from '@/presentation/hooks/useGlobalPeriod';
import {
  formatMoneyCompact,
  formatSigned,
  relativeChangePct,
} from '@/presentation/utils/metricDeltas';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import type { BranchMainMetrics } from './ClinicHubProfessionalsPanel';

export interface ClinicHubFinancesPanelProps {
  tenantId: string;
  period: string;
  year: number;
  month: number;
  summary: MetricsSummary;
  previousSummary: MetricsSummary | null;
  branchMainMetrics: BranchMainMetrics[];
  isBranchMetricsLoading?: boolean;
  /** Sede del hub (URL); sincroniza selección para deep-link a movimientos. */
  hubSedeId?: number | null;
}

type SignalTone = 'up' | 'down' | 'flat';
type BranchFilter = 'all' | 'gain' | 'loss' | 'idle';
type SortKey = 'name' | 'revenue' | 'expense' | 'net' | 'margin';

type BranchStatus = 'gain' | 'loss' | 'idle' | 'flat';

interface BranchFinanceRow {
  id: number;
  name: string;
  revenue: number;
  expense: number;
  net: number;
  margin: number | null;
  status: BranchStatus;
  /** neto actual - neto mes ant.; null si sin base */
  netDelta: number | null;
  trend: 'up' | 'down' | 'flat' | 'none';
}

function toneHigherBetter(delta: number | null, band = 1.5): SignalTone {
  if (delta === null || Math.abs(delta) < band) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

/** Menos egresos es mejor. */
function toneLowerBetter(delta: number | null, band = 1.5): SignalTone {
  if (delta === null || Math.abs(delta) < band) return 'flat';
  return delta < 0 ? 'up' : 'down';
}

function toneClass(tone: SignalTone): string {
  if (tone === 'up') return 'text-teal-700 dark:text-teal-300';
  if (tone === 'down') return 'text-red-700 dark:text-red-300';
  return 'text-slate-500 dark:text-slate-400';
}

function netClass(net: number, status: BranchStatus): string {
  if (status === 'idle') return 'text-slate-500 dark:text-slate-400';
  if (net < 0) return 'text-red-700 dark:text-red-300';
  if (net > 0) return 'text-teal-700 dark:text-teal-300';
  return 'text-gray-800 dark:text-gray-200';
}

function marginPct(net: number, revenue: number): number | null {
  if (revenue <= 0) return null;
  return Number(((net / revenue) * 100).toFixed(1));
}

function classifyBranch(revenue: number, expense: number, net: number): BranchStatus {
  if (revenue === 0 && expense === 0) return 'idle';
  if (net < 0) return 'loss';
  if (net > 0) return 'gain';
  return 'flat';
}

function DualLineChart({
  income,
  expense,
  className,
}: {
  income: number[];
  expense: number[];
  className?: string;
}) {
  const w = 280;
  const h = 72;
  const pad = 4;
  if (income.length < 2 || income.length !== expense.length) {
    return <div className={className} style={{ height: h }} />;
  }
  const max = Math.max(...income, ...expense, 1);
  const min = 0;
  const span = max - min || 1;
  const toPoints = (values: number[]) =>
    values
      .map((v, i) => {
        const x = pad + (i / (values.length - 1)) * (w - pad * 2);
        const y = h - pad - ((v - min) / span) * (h - pad * 2);
        return `${x},${y}`;
      })
      .join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className={className} aria-hidden>
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={toPoints(income)}
        className="text-teal-600 dark:text-teal-400"
      />
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="4 3"
        points={toPoints(expense)}
        className="text-amber-600 dark:text-amber-400"
      />
    </svg>
  );
}

/**
 * Tab Finanzas del hub: caja del período (entró / salió / quedó) + desglose por sucursal.
 * Términos: ingresos/egresos de MovimientoCaja — resultado de caja, no ganancia contable.
 */
export function ClinicHubFinancesPanel({
  tenantId,
  period,
  year,
  month,
  summary,
  previousSummary,
  branchMainMetrics,
  isBranchMetricsLoading,
  hubSedeId = null,
}: ClinicHubFinancesPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { token } = useAuth();

  const [filter, setFilter] = useState<BranchFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('net');
  const [sortDesc, setSortDesc] = useState(false); // neto asc = peores arriba
  const [selectedSede, setSelectedSede] = useState<number | null>(() => hubSedeId);

  useEffect(() => {
    setSelectedSede(hubSedeId);
  }, [hubSedeId]);

  const prev = useMemo(() => previousMonthOf(year, month), [year, month]);
  const prevMonthLabel = useMemo(() => {
    try {
      return new Date(prev.year, prev.month - 1, 1).toLocaleDateString(locale, { month: 'long' });
    } catch {
      return '';
    }
  }, [prev, locale]);

  const trendQuery = useQuery({
    queryKey: metricsKeys.monthlyTrend(tenantId, year, month, 6),
    queryFn: () => getMonthlyTrend(token!, { tenantId, year, month, months: 6 }),
    enabled: Boolean(token && tenantId),
  });
  const trend: MonthlyTrendPoint[] = useMemo(() => trendQuery.data ?? [], [trendQuery.data]);

  const branchPrevQueries = useQueries({
    queries: branchMainMetrics.map((row) => ({
      queryKey: metricsKeys.summary(tenantId, prev.year, prev.month, row.sede.id),
      queryFn: () =>
        getMetricsSummary(token!, {
          tenantId,
          year: prev.year,
          month: prev.month,
          consultorioId: row.sede.id,
        }),
      enabled: Boolean(token && tenantId && branchMainMetrics.length > 0),
    })),
  });

  const prevBySede = useMemo(() => {
    const map: Record<number, MetricsSummary | null> = {};
    branchMainMetrics.forEach((row, i) => {
      const q = branchPrevQueries[i];
      map[row.sede.id] = q?.data ?? null;
    });
    return map;
  }, [branchMainMetrics, branchPrevQueries]);

  const prevLoading = branchPrevQueries.some((q) => q.isLoading);

  const ingresos = summary.caja.ingresos;
  const egresos = summary.caja.egresos;
  const neto = summary.caja.neto;
  const prevIn = previousSummary?.caja.ingresos ?? null;
  const prevEg = previousSummary?.caja.egresos ?? null;
  const prevNet = previousSummary?.caja.neto ?? null;

  const ingresosDelta = prevIn !== null ? relativeChangePct(ingresos, prevIn) : null;
  const egresosDelta = prevEg !== null ? relativeChangePct(egresos, prevEg) : null;
  const netoDelta = prevNet !== null ? relativeChangePct(neto, prevNet) : null;
  const margin = marginPct(neto, ingresos);
  const prevMargin =
    prevNet !== null && prevIn !== null ? marginPct(prevNet, prevIn) : null;
  const marginDelta =
    margin !== null && prevMargin !== null ? Number((margin - prevMargin).toFixed(1)) : null;

  const incomeSeries = useMemo(() => trend.map((p) => p.ingresos), [trend]);
  const expenseSeries = useMemo(() => trend.map((p) => p.egresos), [trend]);

  const branchRows = useMemo((): BranchFinanceRow[] => {
    return branchMainMetrics.map((row) => {
      const revenue = row.summary?.caja.ingresos ?? 0;
      const expense = row.summary?.caja.egresos ?? 0;
      const net = row.summary?.caja.neto ?? revenue - expense;
      const status = classifyBranch(revenue, expense, net);
      const prevNetVal = prevBySede[row.sede.id]?.caja.neto ?? null;
      const netDelta = prevNetVal === null ? null : net - prevNetVal;
      let trend: BranchFinanceRow['trend'] = 'none';
      if (status === 'idle') trend = 'none';
      else if (netDelta === null) trend = 'none';
      else if (Math.abs(netDelta) < 1) trend = 'flat';
      else if (netDelta > 0) trend = 'up';
      else trend = 'down';

      return {
        id: row.sede.id,
        name: row.sede.nombre,
        revenue,
        expense,
        net,
        margin: marginPct(net, revenue),
        status,
        netDelta,
        trend,
      };
    });
  }, [branchMainMetrics, prevBySede]);

  const counts = useMemo(() => {
    return {
      all: branchRows.length,
      gain: branchRows.filter((r) => r.status === 'gain').length,
      loss: branchRows.filter((r) => r.status === 'loss').length,
      idle: branchRows.filter((r) => r.status === 'idle').length,
    };
  }, [branchRows]);

  const filteredSorted = useMemo(() => {
    let list = branchRows;
    if (filter === 'gain') list = list.filter((r) => r.status === 'gain');
    else if (filter === 'loss') list = list.filter((r) => r.status === 'loss');
    else if (filter === 'idle') list = list.filter((r) => r.status === 'idle');

    const sorted = [...list].sort((a, b) => {
      // idle always last unless sorting by name exclusively
      if (sortKey !== 'name') {
        if (a.status === 'idle' && b.status !== 'idle') return 1;
        if (b.status === 'idle' && a.status !== 'idle') return -1;
      }
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name, locale);
      else if (sortKey === 'revenue') cmp = a.revenue - b.revenue;
      else if (sortKey === 'expense') cmp = a.expense - b.expense;
      else if (sortKey === 'margin') {
        const am = a.margin;
        const bm = b.margin;
        if (am === null && bm === null) cmp = 0;
        else if (am === null) cmp = 1;
        else if (bm === null) cmp = -1;
        else cmp = am - bm;
      } else cmp = a.net - b.net;
      if (sortDesc) cmp = -cmp;
      if (cmp === 0) cmp = a.name.localeCompare(b.name, locale);
      return cmp;
    });
    return sorted;
  }, [branchRows, filter, sortKey, sortDesc, locale]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc((v) => !v);
    else {
      setSortKey(key);
      // neto y margen: asc (peores primero); resto desc
      setSortDesc(key === 'name' || key === 'revenue' || key === 'expense');
    }
  };

  const movimientosHref = useMemo(
    () =>
      buildCajaMovimientosHref({
        tenantId,
        year,
        month,
        period,
        sede: selectedSede ?? undefined,
        from: 'clinicHub',
      }),
    [tenantId, period, year, month, selectedSede]
  );

  const selectedName =
    selectedSede === null
      ? null
      : branchRows.find((r) => r.id === selectedSede)?.name ?? null;

  return (
    <div className="space-y-8 pb-8">
      {/* KPI strip */}
      <section aria-label={t('clinicHub.financesTab.kpisAria')}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <Kpi
            value={formatMoneyCompact(locale, ingresos)}
            label={t('clinicHub.financesTab.kpis.income')}
            sub={
              prevIn !== null
                ? t('clinicHub.financesTab.vsAbsolute', {
                    value: formatMoneyCompact(locale, prevIn),
                    month: prevMonthLabel,
                  })
                : null
            }
            delta={
              ingresosDelta === null
                ? null
                : t('clinicHub.financesTab.deltaPct', {
                    value: formatSigned(locale, ingresosDelta),
                  })
            }
            tone={toneHigherBetter(ingresosDelta)}
          />
          <Kpi
            value={formatMoneyCompact(locale, egresos)}
            label={t('clinicHub.financesTab.kpis.expense')}
            sub={
              prevEg !== null
                ? t('clinicHub.financesTab.vsAbsolute', {
                    value: formatMoneyCompact(locale, prevEg),
                    month: prevMonthLabel,
                  })
                : null
            }
            delta={
              egresosDelta === null
                ? null
                : t('clinicHub.financesTab.deltaPct', {
                    value: formatSigned(locale, egresosDelta),
                  })
            }
            tone={toneLowerBetter(egresosDelta)}
          />
          <Kpi
            value={`${neto > 0 ? '+' : ''}${formatMoneyCompact(locale, neto)}`}
            label={t('clinicHub.financesTab.kpis.net')}
            sub={
              prevNet !== null
                ? t('clinicHub.financesTab.vsAbsolute', {
                    value: `${prevNet > 0 ? '+' : ''}${formatMoneyCompact(locale, prevNet)}`,
                    month: prevMonthLabel,
                  })
                : null
            }
            delta={
              netoDelta === null
                ? null
                : t('clinicHub.financesTab.deltaPct', {
                    value: formatSigned(locale, netoDelta),
                  })
            }
            tone={toneHigherBetter(netoDelta)}
            valueClass={neto < 0 ? 'text-red-700 dark:text-red-300' : neto > 0 ? 'text-teal-700 dark:text-teal-300' : undefined}
            hint={t('clinicHub.financesTab.kpis.netHint')}
          />
          <Kpi
            value={
              margin === null
                ? '—'
                : `${margin.toLocaleString(locale, { maximumFractionDigits: 1 })}%`
            }
            label={t('clinicHub.financesTab.kpis.margin')}
            delta={
              marginDelta === null
                ? null
                : t('clinicHub.financesTab.deltaPts', {
                    value: formatSigned(locale, marginDelta),
                  })
            }
            tone={toneHigherBetter(marginDelta, 0.5)}
            hint={t('clinicHub.financesTab.kpis.marginHint')}
          />
        </div>
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
          {neto >= 0
            ? t('clinicHub.financesTab.verdict.positive', {
                net: formatMoneyCompact(locale, neto),
              })
            : t('clinicHub.financesTab.verdict.negative', {
                net: formatMoneyCompact(locale, Math.abs(neto)),
              })}
        </p>
      </section>

      {/* Evolución */}
      {incomeSeries.length >= 2 ? (
        <section className="space-y-2 border-t border-[var(--bi-line)] pt-6">
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
            {t('clinicHub.financesTab.trend.title')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('clinicHub.financesTab.trend.hint')}
          </p>
          <div className="flex flex-wrap items-end gap-6">
            <DualLineChart income={incomeSeries} expense={expenseSeries} />
            <div className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
              <p>
                <span className="mr-2 inline-block h-0.5 w-4 bg-teal-600 align-middle dark:bg-teal-400" />
                {t('clinicHub.financesTab.trend.income')}
              </p>
              <p>
                <span className="mr-2 inline-block h-0.5 w-4 border-t border-dashed border-amber-600 align-middle dark:border-amber-400" />
                {t('clinicHub.financesTab.trend.expense')}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {/* Por sucursal */}
      {branchMainMetrics.length > 0 ? (
        <section className="space-y-3 border-t border-[var(--bi-line)] pt-6">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                {t('clinicHub.financesTab.branches.title')}
              </h2>
              {counts.loss > 0 ? (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {t('clinicHub.financesTab.branches.lossSummary', {
                    loss: counts.loss,
                    total: counts.all,
                  })}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {(
              [
                ['all', counts.all],
                ['gain', counts.gain],
                ['loss', counts.loss],
                ['idle', counts.idle],
              ] as const
            ).map(([id, count]) => {
              const active = filter === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition ${
                    active
                      ? 'border-teal-600 bg-teal-50 text-teal-800 dark:border-teal-500 dark:bg-teal-950/40 dark:text-teal-200'
                      : 'border-[var(--bi-line)] bg-transparent text-gray-700 hover:bg-slate-50 dark:text-gray-200 dark:hover:bg-white/5'
                  }`}
                >
                  {t(`clinicHub.financesTab.branches.filters.${id}`, { count })}
                </button>
              );
            })}
          </div>

          {isBranchMetricsLoading || prevLoading ? (
            <LoadingBlock size="sm" minHeightClassName="min-h-[5rem]" />
          ) : filteredSorted.length === 0 ? (
            <p className="text-sm text-gray-500">{t('clinicHub.financesTab.branches.emptyFilter')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    {(
                      [
                        ['name', t('clinicHub.financesTab.branches.cols.branch')],
                        ['revenue', t('clinicHub.financesTab.branches.cols.income')],
                        ['expense', t('clinicHub.financesTab.branches.cols.expense')],
                        ['net', t('clinicHub.financesTab.branches.cols.net')],
                        ['margin', t('clinicHub.financesTab.branches.cols.margin')],
                      ] as const
                    ).map(([key, label]) => (
                      <th key={key} className="pb-2 pr-3 font-semibold">
                        <button
                          type="button"
                          className="hover:text-teal-700 dark:hover:text-teal-300"
                          onClick={() => toggleSort(key)}
                        >
                          {label}
                          {sortKey === key ? (sortDesc ? ' ↓' : ' ↑') : ''}
                        </button>
                      </th>
                    ))}
                    <th className="pb-2 font-semibold">
                      {t('clinicHub.financesTab.branches.cols.trend')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((row) => {
                    const selected = selectedSede === row.id;
                    return (
                      <tr
                        key={row.id}
                        className={`cursor-pointer border-t border-[var(--bi-line)] first:border-0 ${
                          selected
                            ? 'bg-teal-50/60 dark:bg-teal-950/20'
                            : 'hover:bg-slate-50/80 dark:hover:bg-white/5'
                        }`}
                        onClick={() =>
                          setSelectedSede((cur) => (cur === row.id ? null : row.id))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedSede((cur) => (cur === row.id ? null : row.id));
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={selected}
                        aria-label={t('clinicHub.financesTab.branches.selectAria', {
                          name: row.name,
                        })}
                      >
                        <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                          {row.name}
                          {row.status === 'idle' ? (
                            <span className="ml-2 text-[11px] font-normal text-slate-500">
                              {t('clinicHub.financesTab.branches.idleBadge')}
                            </span>
                          ) : row.status === 'loss' ? (
                            <span className="ml-2 text-[11px] font-semibold text-red-700 dark:text-red-300">
                              {t('clinicHub.financesTab.branches.lossBadge')}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3 tabular-nums text-gray-800 dark:text-gray-200">
                          {row.status === 'idle'
                            ? '—'
                            : formatMoneyCompact(locale, row.revenue)}
                        </td>
                        <td className="py-2 pr-3 tabular-nums text-gray-800 dark:text-gray-200">
                          {row.status === 'idle'
                            ? '—'
                            : formatMoneyCompact(locale, row.expense)}
                        </td>
                        <td
                          className={`py-2 pr-3 font-semibold tabular-nums ${netClass(row.net, row.status)}`}
                        >
                          {row.status === 'idle'
                            ? '—'
                            : `${row.net > 0 ? '+' : ''}${formatMoneyCompact(locale, row.net)}`}
                        </td>
                        <td className="py-2 pr-3 tabular-nums text-gray-700 dark:text-gray-300">
                          {row.margin === null
                            ? '—'
                            : `${row.margin.toLocaleString(locale, { maximumFractionDigits: 1 })}%`}
                        </td>
                        <td className="py-2 text-gray-700 dark:text-gray-300">
                          {row.status === 'idle'
                            ? t('clinicHub.financesTab.branches.idleBadge')
                            : row.trend === 'up'
                              ? '↑'
                              : row.trend === 'down'
                                ? '↓'
                                : row.trend === 'flat'
                                  ? '→'
                                  : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {selectedName ? (
            <p className="text-xs text-teal-800 dark:text-teal-300">
              {t('clinicHub.financesTab.branches.selected', { name: selectedName })}
            </p>
          ) : null}
        </section>
      ) : null}

      <div className="border-t border-[var(--bi-line)] pt-4">
        <Link
          to={movimientosHref}
          className="text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {selectedName
            ? t('clinicHub.financesTab.openMovementsBranch', { name: selectedName })
            : t('clinicHub.financesTab.openMovements')}{' '}
          →
        </Link>
      </div>
    </div>
  );
}

function Kpi({
  value,
  label,
  sub = null,
  delta,
  tone,
  hint,
  valueClass,
}: {
  value: string;
  label: string;
  sub?: string | null;
  delta: string | null;
  tone: SignalTone;
  hint?: string;
  valueClass?: string;
}) {
  return (
    <div>
      <p
        className={`text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl ${
          valueClass ?? 'text-gray-950 dark:text-white'
        }`}
      >
        {value}
      </p>
      <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{label}</p>
      {sub ? <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{sub}</p> : null}
      {delta ? <p className={`mt-0.5 text-xs font-medium ${toneClass(tone)}`}>{delta}</p> : null}
      {hint ? <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{hint}</p> : null}
    </div>
  );
}
